import { equals, map, mergeRight } from "ramda";

import dateCore, { type DateCore } from "helpers/date/dateCore";
import TIMEZONES from "helpers/timezones";
import {
  isStoreInfoRedirect,
  type OloStoreInfoResponse,
} from "interfaces/olo_store_info";
import type {
  Fulfillment,
  FulfillmentSchedule,
  StoreInfo,
  StoreInfoRedirect,
  TimeRange,
} from "types/reducers/store_info";

const SCHEDULE_BASE: ScheduleParsed = {
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
};

const SORT_DAYS = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

const DAY_NAMES = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

/**
 * Wall-clock time "today" in the store zone
 * Stored as DateCore for the rest of the app.
 */
const convertToZonedDateCore = (
  timezone: string,
  time: [string, string]
): DateCore => {
  const tz = TIMEZONES[timezone];
  return dateCore()
    .tz(tz)
    .hour(parseInt(time[0], 10))
    .minute(parseInt(time[1], 10))
    .second(0)
    .millisecond(0);
};

// Example: 10pm - 2am
// They day key will be correct for the first
// DateCore but not the second
function correctRangesSpanningTwoDays(
  range: TimeRange<DateCore>
): TimeRange<DateCore> {
  if (range[0].isAfter(range[1])) {
    return [range[0], range[1].add(1, "day")];
  }

  return range;
}

const formatDeliveryWindows = (
  timezone: string,
  ranges: TimeRange<string>[],
  key: string
): FulfillmentSchedule => {
  const timesAsDateCore: TimeRange<DateCore>[] = ranges.map(range => {
    return range.map(time => {
      return convertToZonedDateCore(
        timezone,
        time.split(":") as [string, string]
      );
    }) as [DateCore, DateCore];
  });
  return {
    day: key,
    order: SORT_DAYS[key],
    timeRanges: map(correctRangesSpanningTwoDays, timesAsDateCore),
    prettyName: DAY_NAMES[key],
  };
};

const scheduleTransform = (
  timezone: string,
  schedule: ScheduleParsed
): FulfillmentSchedule[] => {
  return Object.entries(schedule)
    .map(([key, ranges]) => {
      return formatDeliveryWindows(timezone, ranges, key);
    })
    .sort(({ order: orderA }, { order: orderB }) => {
      return orderA - orderB;
    });
};

const addTransportCopy = (
  timezone: string,
  prepTime: [number, number] | number,
  transportOption: Fulfillment<ScheduleParsed>
): Fulfillment => {
  const completeSchedule: ScheduleParsed = mergeRight(
    SCHEDULE_BASE,
    transportOption.schedule
  );
  let estimateRange = [30, 40];
  if (prepTime) {
    if (Array.isArray(prepTime)) {
      estimateRange = prepTime;
    } else {
      estimateRange = [prepTime, prepTime + 10];
    }
  }
  if (equals(transportOption.type, "delivery")) {
    return {
      ...transportOption,
      name: "Delivery",
      estimate: estimateRange,
      estimateCopy: "Est Delivery",
      schedule: scheduleTransform(timezone, completeSchedule),
    };
  }
  if (equals(transportOption.type, "dine_in")) {
    return {
      ...transportOption,
      name: "Dine in",
      estimate: estimateRange,
      estimateCopy: "Est Dine in",
      schedule: scheduleTransform(timezone, completeSchedule),
    };
  }
  return {
    ...transportOption,
    name: "Pick up",
    estimate: estimateRange,
    estimateCopy: "Ready for pick up",
    schedule: scheduleTransform(timezone, completeSchedule),
  };
};

const extendFulfillmentTypes = (
  storeInfo: OloStoreInfoResponseParsed
): StoreInfo => {
  return {
    ...storeInfo,
    fulfillmentTypes: map(transportOption => {
      const { type } = transportOption;
      const orderReady = storeInfo[`${type}OrderReadyBy`];
      const orderReadyMax = storeInfo[`${type}OrderReadyByMax`];
      if (orderReady && orderReadyMax) {
        return addTransportCopy(
          storeInfo.timezone,
          [orderReady, orderReadyMax],
          transportOption
        );
      }
      return addTransportCopy(
        storeInfo.timezone,
        storeInfo.orderReadyBy,
        transportOption
      );
    }, storeInfo.fulfillmentTypes),
  };
};

type ScheduleParsed = Record<string, TimeRange<string>[]>;

type OloStoreInfoResponseParsed = StoreInfo<Fulfillment<ScheduleParsed>>;

export default function oloStoreInfoTransform(
  storePrettyUrl: string,
  storeInfo: OloStoreInfoResponse | StoreInfoRedirect
): StoreInfo | StoreInfoRedirect {
  if (isStoreInfoRedirect(storeInfo)) {
    return storeInfo;
  }

  const parsedInfo: OloStoreInfoResponseParsed = {
    ...storeInfo,
    prettyUrl: storePrettyUrl,
    fulfillmentTypes: map(fulfillmentType => {
      return {
        ...fulfillmentType,
        schedule: JSON.parse(fulfillmentType.schedule),
      };
    }, storeInfo.fulfillmentTypes),
  };

  return extendFulfillmentTypes(parsedInfo);
}

export { SORT_DAYS, scheduleTransform, extendFulfillmentTypes };
